<script setup lang="ts">
import type { EditorView } from "@codemirror/view";
import { computed, nextTick, ref, watch } from "vue";
import { wrapQR } from "../../common/render.common";
import type { HotspotTemplate } from "../../common/types.common";
import { defineActions, defineState } from "../common";
import { FormInputNumeric } from "../input";
import { FormMessage, FormTool } from "../internal";
import FormPanel from "../internal/FormPanel.vue";
import { type BaseProps, defaultProps } from "../props";

type ModelType = HotspotTemplate;
interface PropsType extends BaseProps<ModelType> {
    placeholder?: string;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    value: undefined,
});

const model = defineModel<ModelType>({
    required: true,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);
const view = ref<EditorView>();

const preview = ref<string>();
const printed = ref<string>();

const outerStyle = computed(() => {
    return { height: model.value.height + 2 + "cm" };
});
const innerStyle = computed(() => {
    return { width: model.value.width + "cm", height: model.value.height + "cm" };
});

async function prepare() {
    await nextTick();
    const { css } = await import("@codemirror/lang-css");
    const { html } = await import("@codemirror/lang-html");
    const { EditorView } = await import("@codemirror/view");
    const { basicSetup } = await import("codemirror");

    view.value = new EditorView({
        doc: model.value.code,
        parent: document.getElementById(state.id)!,
        extensions: [
            basicSetup,
            html(),
            css().support,
            EditorView.theme({
                ".cm-content *": {
                    fontSize: "12px",
                    fontFamily: "'Google Sans Code Variable', monospace",
                },
                ".cm-gutter *": {
                    fontSize: "12px",
                    fontFamily: "'Google Sans Code Variable', monospace",
                },
            }),
            EditorView.updateListener.of(function (e) {
                if (e.docChanged) {
                    model.value.code = e.state.doc.toString();
                }
            }),
        ],
    });
}

if (!import.meta.env.SSR) {
    watch(
        () => state.editMode,
        async () => {
            if (!state.editMode) return;
            await prepare();
        },
        { immediate: true },
    );
}

watch(
    () => [model.value.code, model.value.width, model.value.height],
    async () => {
        if (import.meta.env.SSR) return;
        const compile = (await import("handlebars")).compile;
        const sanitize = (await import("isomorphic-dompurify")).sanitize;
        const QRCode = await import("qrcode");
        const template = compile(sanitize(model.value.code), { noEscape: true });
        const login = "http://hotspot.ring.net/login?username=NJ65KHS&password=NJ65KHS";
        const qrcode = await QRCode.toDataURL(login, { margin: 2, scale: 8 });

        preview.value = template({
            code: "{{code}}",
            valid: "{{valid}}",
            active: "{{active}}",
            price: "{{price}}",
            width: model.value.width,
            height: model.value.height,
            qr: "{{qr}}",
        });

        printed.value = template({
            code: "NJ65KHS",
            valid: "6 JAM",
            active: "12 HARI",
            price: "13.000",
            width: model.value.width,
            height: model.value.height,
            qr: wrapQR(`<img src='${qrcode}'>`),
        });
    },
    { immediate: true },
);
</script>

<template>
    <FormPanel :param="props">
        <template #header>
            <span class="inline-flex flex-row items-center gap-3">
                <span class="font-semibold text-gray-600 inline-flex flex-row gap-2 items-center">
                    {{ props.label ?? "Pengaturan Template" }}
                </span>
            </span>
        </template>

        <template #icons>
            <span class="inline-flex flex-row">
                <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
            </span>
        </template>

        <div class="flex flex-col gap-4">
            <template v-if="!state.editMode">
                <div class="flex flex-col md:flex-row gap-2">
                    <div class="rns-preview grow" :style="outerStyle">
                        <div class="rns-voucher" :style="innerStyle" v-html="preview"></div>
                    </div>
                    <div class="rns-preview grow" :style="outerStyle">
                        <div class="rns-voucher" :style="innerStyle" v-html="printed"></div>
                        <div class="absolute top-1 left-2 font-bold">CONTOH</div>
                    </div>
                </div>

                <div class="flex flex-col md:flex-row gap-2">
                    <FormInputNumeric
                        v-model="model.width"
                        class="grow"
                        embedMode="embed"
                        fraction
                        label="Panjang"
                        :max="10"
                        :min="3"
                        :mode="state.nestMode"
                        unit="cm"
                    />
                    <FormInputNumeric
                        v-model="model.height"
                        class="grow"
                        embedMode="embed"
                        fraction
                        label="Lebar"
                        :max="10"
                        :min="2"
                        :mode="state.nestMode"
                        unit="cm"
                    />
                </div>
            </template>
            <template v-else>
                <div class="flex flex-col md:flex-row gap-2">
                    <div class="rns-preview grow" :style="outerStyle">
                        <div class="rns-voucher" :style="innerStyle" v-html="preview"></div>
                    </div>
                    <div class="rns-preview grow" :style="outerStyle">
                        <div class="rns-voucher" :style="innerStyle" v-html="printed"></div>
                        <div class="absolute top-1 left-2 font-bold">CONTOH</div>
                    </div>
                </div>

                <div class="flex flex-col md:flex-row gap-2">
                    <div class="w-full md:w-5/12 flex flex-col gap-3 justify-center">
                        <FormInputNumeric
                            v-model="model.width"
                            class="w-full md:w-auto"
                            embedMode="embed"
                            fraction
                            label="Panjang"
                            :max="10"
                            :min="3"
                            unit="cm"
                        />
                        <FormInputNumeric
                            v-model="model.height"
                            class="w-full md:w-auto"
                            embedMode="embed"
                            fraction
                            label="Lebar"
                            :max="10"
                            :min="2"
                            unit="cm"
                        />
                    </div>

                    <!-- prettier-ignore -->
                    <table v-pre class="w-full md:w-7/12 rns-table-guide">
                        <thead>
                            <tr>
                                <th>KODE</th>
                                <th>KETERANGAN</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>{{code}}</code></td>
                                <td>Nama pengguna / kode voucher</td>
                            </tr>
                            <tr>
                                <td><code>{{valid}}</code></td>
                                <td>Batas waktu penggunaan</td>
                            </tr>
                            <tr>
                                <td><code>{{active}}</code></td>
                                <td>Batas waktu kadaluarsa</td>
                            </tr>
                            <tr>
                                <td><code>{{price}}</code></td>
                                <td>Harga voucher</td>
                            </tr>
                            <tr>
                                <td><code>{{qr}}</code></td>
                                <td>Kode QR</td>
                            </tr>
                            <tr>
                                <td><code>{{width}}</code></td>
                                <td>Panjang voucher (cm)</td>
                            </tr>
                            <tr>
                                <td><code>{{height}}</code></td>
                                <td>Lebar voucher (cm)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div :id="state.id" class="border border-slate-400"></div>
            </template>

            <FormMessage :state="state" />
        </div>
    </FormPanel>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-preview {
    @apply bg-white border relative flex items-center justify-center;
}

.rns-voucher {
    @apply border-2 border-dashed border-slate-700 shadow overflow-hidden;
}

.rns-table-guide {
    @apply text-xs;
}

.rns-table-guide code {
    @apply font-black;
}

.rns-table-guide th,
.rns-table-guide td {
    @apply border px-3 py-1;
}

.rns-table-guide td:first-child {
    @apply text-center w-2/12;
}
</style>
